import type { CSSProperties, ReactElement } from 'react';
import { COLOR_THEMES, type ColorTheme } from '../hooks/useColorTheme';
import { buildClassName } from './buildClassName';

function SunIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" focusable="false">
      <path
        fill="currentColor"
        d="M11.1 22v-.91a.9.9 0 0 1 1.8 0V22a.9.9 0 0 1-1.8 0M5 17.727A.9.9 0 1 1 6.272 19l-.909.91a.9.9 0 1 1-1.272-1.274zm12.728 0a.9.9 0 0 1 1.272 0l.91.909a.9.9 0 0 1-1.274 1.273L17.727 19a.9.9 0 0 1 0-1.273M16.554 12a4.555 4.555 0 1 0-9.11 0 4.555 4.555 0 0 0 9.11 0m-13.646-.9a.9.9 0 0 1 0 1.8H2a.9.9 0 0 1 0-1.8zM22 11.1a.9.9 0 0 1 0 1.8h-.91a.9.9 0 0 1 0-1.8zM4.09 4.09a.9.9 0 0 1 1.273 0l.91.91A.9.9 0 1 1 5 6.272l-.91-.909a.9.9 0 0 1 0-1.272m14.546 0a.9.9 0 0 1 1.273 1.273l-.909.91A.9.9 0 1 1 17.727 5zM11.1 2.91V2a.9.9 0 0 1 1.8 0v.91a.9.9 0 0 1-1.8 0M18.355 12a6.354 6.354 0 1 1-12.71 0 6.354 6.354 0 0 1 12.71 0"
      />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg fill="none" viewBox="0 0 24 24" focusable="false">
      <path
        fill="currentColor"
        d="M7.874 2.194a.9.9 0 0 1 1.3.806c0 4.116 1.02 7.028 2.909 8.917s4.8 2.91 8.917 2.91a.9.9 0 0 1 .806 1.299 10.39 10.39 0 0 1-9.313 5.774C6.753 21.9 2.1 17.247 2.1 11.507c0-4.082 2.354-7.613 5.774-9.313M3.9 11.507a8.593 8.593 0 0 0 15.533 5.068c-3.65-.236-6.562-1.325-8.622-3.386C8.75 11.13 7.66 8.217 7.423 4.566a8.58 8.58 0 0 0-3.524 6.94"
      />
    </svg>
  );
}

interface ThemeEntry {
  theme: ColorTheme;
  accessibleLabel: string;
  Icon: () => ReactElement;
}

const THEME_ENTRIES: ThemeEntry[] = [
  { theme: COLOR_THEMES.LIGHT, accessibleLabel: '라이트 모드', Icon: SunIcon },
  { theme: COLOR_THEMES.DARK, accessibleLabel: '다크 모드', Icon: MoonIcon },
];

const ignoreThemeChange = (): void => {};

export interface ThemeToggleProps {
  theme?: ColorTheme;
  onThemeChange?: (theme: ColorTheme) => void;
}

export function ThemeToggle({ theme = COLOR_THEMES.LIGHT, onThemeChange = ignoreThemeChange }: ThemeToggleProps) {
  const selectedIndex = THEME_ENTRIES.findIndex((entry) => entry.theme === theme);
  return (
    <div className="theme-toggle" role="group" aria-label="화면 테마">
      <span
        className="theme-toggle__thumb"
        aria-hidden="true"
        // 커스텀 프로퍼티는 CSSProperties에 없어서 여기서 한 번 좁혀 줍니다.
        style={{ '--tds-theme-index': Math.max(selectedIndex, 0) } as CSSProperties}
      />
      {THEME_ENTRIES.map((entry) => {
        const isSelected = entry.theme === theme;
        const Icon = entry.Icon;
        return (
          <button
            key={entry.theme}
            type="button"
            className={buildClassName('theme-toggle__button', 'theme-toggle__button--selected', isSelected)}
            aria-label={entry.accessibleLabel}
            aria-pressed={isSelected}
            onClick={() => onThemeChange(entry.theme)}
          >
            <span className="theme-toggle__icon" aria-hidden="true">
              <Icon />
            </span>
          </button>
        );
      })}
    </div>
  );
}
